"use client";

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Link from "next/link";
import { FiX } from "react-icons/fi";
import config from "../../config/config";

export default function PollsFloatingCard() {
  const [pollsData, setPollsData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(true); // Always start as true
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false); // Track if scroll detection started
  
  const currentLanguage = useSelector((state) => state.language.language || 'en');

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
          const scrollPercentage = (currentScrollY / documentHeight) * 100;
          
          // Show card only after 10% scroll
          if (scrollPercentage >= 10) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
          
          const scrollDifference = Math.abs(currentScrollY - lastScrollY);
          
          // Only update scroll direction if scroll difference is significant (reduces flickering)
          if (scrollDifference > 50) {
            if (currentScrollY < lastScrollY) {
              // Scrolling up
              setIsScrollingUp(true);
            } else if (currentScrollY > lastScrollY && currentScrollY > 200) {
              // Scrolling down (hide after 200px with bigger threshold)
              setIsScrollingUp(false);
            }
            
            setLastScrollY(currentScrollY);
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  useEffect(() => {
    const fetchPollsFloatingData = async () => {
      try {
        // Ensure language is not undefined, fallback to 'en'
        const language = currentLanguage || 'en';
        const response = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/polls-floating?language=${language}`
        );
        const result = await response.json();
        
        console.log('Polls Floating API Response:', result);
        
        if (result.success && result.data) {
          // Show if explicitly enabled is true
          const shouldShow = result.data.enabled === true;
          
          console.log('Should show polls floating:', shouldShow);
          console.log('API enabled value:', result.data.enabled, 'type:', typeof result.data.enabled);
          
          if (shouldShow) {
            setPollsData(result.data);
            console.log('Polls data set:', result.data);
            
            // Clear sessionStorage for testing - always show card
            const dismissedKey = `polls-floating-dismissed-session`;
            sessionStorage.removeItem(dismissedKey);
            console.log('Cleared session storage');
            
            // Don't auto-show anymore - wait for scroll
            console.log('Polls data loaded, waiting for 10% scroll to show');
          }
        }
      } catch (error) {
        console.error("Error fetching polls floating data:", error);
      }
    };

    fetchPollsFloatingData();
  }, [currentLanguage]); // Re-run when language changes

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    
    // Store dismissal in sessionStorage for current session
    const dismissedKey = `polls-floating-dismissed-session`;
    sessionStorage.setItem(dismissedKey, 'true');
  };

  console.log('Polls Floating Debug:', {
    pollsData: !!pollsData,
    isVisible,
    isDismissed,
    currentLanguage
  });

  if (!pollsData) {
    console.log('Not showing: no pollsData');
    return null;
  }
  if (!isVisible) {
    console.log('Not showing: not visible');
    return null;
  }
  if (isDismissed) {
    console.log('Not showing: dismissed');
    return null;
  }

  return (
    <>
      {/* Floating card */}
      <div 
        className={`fixed bottom-20 md:bottom-5 right-5 w-20 sm:w-24 md:w-28 z-50 transition-all duration-700 ease-in-out transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        {/* Close Button - Outside card, top left corner */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          className="absolute -top-1.5 -left-1.5 z-20 w-4 h-4 bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
          aria-label="Close"
        >
          <FiX className="w-2 h-2 text-white dark:text-gray-800" />
        </button>

        <div
          className={`bg-transparent overflow-hidden rounded-md ${
            pollsData.link ? 'cursor-pointer hover:scale-[1.02]' : ''
          } transition-all duration-300`}
          onClick={() => {
            if (pollsData.link) {
              // Hide the floating card when clicked
              handleDismiss();
              
              if (pollsData.openInNewTab) {
                window.open(pollsData.link, '_blank', 'noopener,noreferrer');
              } else {
                window.location.href = pollsData.link;
              }
            }
          }}
        >
          {/* Square Image Container - Full Card is just the image */}
          {(pollsData.desktopImage || pollsData.mobileImage) && (
            <div className="relative w-full aspect-square overflow-hidden">
              
              {/* Desktop Image */}
              <img
                src={pollsData.desktopImage}
                alt="Live Polls"
                className="hidden md:block w-full h-full object-contain relative z-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              
              {/* Mobile Image with fallback to desktop */}
              <img
                src={pollsData.mobileImage || pollsData.desktopImage}
                alt="Live Polls"
                className="md:hidden w-full h-full object-contain relative z-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}